import styled from "styled-components/macro";
import {createRef} from "react";
import {useToast} from "@chakra-ui/react";
import {useStoreSavedFeed, useUsername} from "../store/hooks";
import {useActions, useLoggedIn, useModal} from "../store";
import {useSaveFeed} from "../api/custom_feeds";
import {useFeedback} from "../store/feedback";
import NavLink from "./nav_link";
import FeedSettings from "./feed_settings";
import {themecolors} from "../utils/themecolors";
import {ChevronLeft, ChevronRight} from "lucide-react";

const SUGGESTED_FEEDS = [
    {
        name: 'Recommended',
        id: 1,
    },
    {
        name: 'All',
        id: 2,
    },
    {
        name: 'Memes',
        id: 4,
    },
    {
        name: 'Discussion',
        id: 5,
    },
    {
        name: 'COVID',
        id: 43,
    },
    {
        name: 'News',
        id: 6,
    },
    {
        name: 'Crypto',
        id: 14,
    },
    {
        name: 'Hobbies',
        id: 7,
    },
    {
        name: 'Gaming',
        id: 8,
    },
    {
        name: 'Technology',
        id: 17,
    },
    {
        name: 'Animals',
        id: 9,
    },
    {
        name: 'Videos',
        id: 19,
    },
    {
        name: 'Finance',
        id: 10,
    },
    {
        name: 'Conspiracies',
        id: 11,
    },
    {
        name: 'Sports',
        id: 20,
    },
    {
        name: 'Entertainment',
        id: 12,
    },
    {
        name: 'Politics',
        id: 13,
    },
    {
        name: 'Censorship',
        id: 15,
    },
    {
        name: 'Privacy',
        id: 18,
    },
    {
        name: 'Streaming',
        id: 16,
    },
];

const FeedBarOuter = styled.div``;
const FeedBarInner = styled.div``;
const FeedDescription = styled.div``;
const FeedTitle = styled.div``;
const SaveFeed = styled.div``;
const EditFeed = styled.div``;
const FeedBarActive = styled.div``;
const FeedBarArrow = styled.div``;
const FeedBarSuggestions = styled.div``;
const FeedBarItems = styled.div``;
const FeedBarItem = styled.div``;

const FeedBar = styled(({className, feed}) => {

    const username = useUsername();
    const loggedIn = useLoggedIn();
    const modal = useModal();

    const isSaved = useStoreSavedFeed(feed?.id);

    const navRef = createRef();
    const handleNav = (direction) => {
        if (direction === 'left') {
            navRef ? (navRef.current.scrollLeft -= 300) : null;
        } else {
            navRef ? (navRef.current.scrollLeft += 300) : null;
        }
    }

    const {error, feedbackCh} = useFeedback();
    const saveFeed = useSaveFeed(feedbackCh);
    const toast = useToast();
    const actions = useActions();
    const handleSave = async () => {
        try {
            await saveFeed(feed.id, !isSaved);
            toast({
                title: `You ${!isSaved ? 'saved' : 'unsaved'} the feed.`,
                status: 'success',
                isClosable: true,
            })
            isSaved ? actions.savedFeedsRemoved(feed.id) : actions.savedFeedsAdded(feed);
        } catch {
            // error is displayed
        }
    };

    const handleUpdate = (title, description) => {
        feed.feed_name = title;
        feed.description = description;
    }

    const handleEdit = () => {
        modal.open(FeedSettings, {currentFeed: feed, handleUpdate});
    };

    return (
                <FeedBarOuter className={className}>
                    <FeedBarInner>
                        <FeedBarArrow onClick={() => handleNav('left')}>
                            <ChevronLeft size={22} />
                        </FeedBarArrow>
                        <FeedBarSuggestions ref={navRef}>
                            <FeedBarItems>
                                {SUGGESTED_FEEDS?.map((suggestedFeed) => (
                                    <NavLink href={`/feed/${suggestedFeed.id}/${suggestedFeed.name.toLowerCase()}-feed/`} react_route key={suggestedFeed.id}>
                                        <FeedBarItem className={suggestedFeed.id === feed.id ? 'active-feed' : ''}><img src={`https://media.scored.co/feed-icons/feed_${suggestedFeed.name.toLowerCase()}_42.png?q=2`} />{suggestedFeed.name}</FeedBarItem>
                                    </NavLink>
                                ))}
                            </FeedBarItems>
                        </FeedBarSuggestions>
                        <FeedBarArrow onClick={() => handleNav('right')}>
                            <ChevronRight size={22} />
                        </FeedBarArrow>
                    </FeedBarInner>
                </FeedBarOuter>
    );
})`  
  padding-bottom: 8px;
  ${FeedBarInner} {
    max-width: ${({theme}) => theme.fullWidth ? '100%' : '1360px'};
    margin: 0 auto;
    font-size: 18px;
    font-weight: 500;
    display: flex;
    height: 42px;
    align-items: center;
  }
  ${FeedBarActive} {
    margin-right: 24px;
    max-width: 332px;
    white-space: nowrap;
  }
  ${FeedTitle} {
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ${SaveFeed}, ${EditFeed} {
    margin-left: 8px;
    cursor: pointer;
    font-size: 13px;
  }
  ${FeedDescription} {
    font-weight: 400;
    font-size: 13px;
    color: #a89e9e;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ${FeedBarArrow} {
    padding: 4px;
    border-radius: 4px;
    font-size: 13px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ${FeedBarArrow}:last-of-type {
    margin-left: auto;
  }
  
  ${FeedBarSuggestions} {
    display: flex;
    height: 36px;
    flex-grow: 1;
    overflow: hidden;
  }
  
  ${FeedBarItem} {
    border-radius: 24px;
    padding: 6px 16px;
    font-size: 15px;
    font-weight: 400;
    cursor: pointer;
    white-space: nowrap;
    margin: 0 4px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_3 : 'white'};
    display: flex;
    align-items: center;
  }
  
  ${FeedBarItem} img {
    width: 20px;
    margin-right: 6px;
    border-radius: 50%;
  }

  ${FeedBarItems} {
    flex-grow: 1;
    display: flex;
    overflow: hidden;
    flex-shrink: 0;
  }
  .active-feed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.white_1.color : '#444'};
    color: ${({theme}) => theme.color.name === 'dark' ? 'black' : 'white'};
  }

  @media only screen and (max-width: 780px) {
    & {
      margin-bottom: 0;
      border-bottom: 1px solid ${({theme}) => theme.color.name === 'dark' ? theme.dark_border : "#dddd"};
      padding-bottom: 0;
      background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : 'transparent'};
    }
    ${FeedBarInner} {
      height: auto;
      padding: 16px 22px;
    }
    ${FeedBarActive} {
      margin-bottom: 12px;
    }
  }
  
`;

export default FeedBar;
